import { useTranslation } from 'react-i18next'
import type { PackagingSupplier } from '@/shared/hooks/api/packagingSuppliers'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { funnelStageLabelKey } from './packagingSupplierOverviewColumns'
import {
  derivePackagingSupplierFunnelStage,
  formatStatFraction,
} from './packagingSupplierStats'

type PackagingSupplierFunnelStatusCellProps = {
  supplier: Pick<
    PackagingSupplier,
    | 'onboardingStatus'
    | 'complianceProgress'
    | 'allPackedItemsComplete'
    | 'magicLinkFirstOpenedAt'
    | 'ppwrComplianceAcknowledgedAt'
    | 'completedPackagingItemCount'
    | 'totalPackagingItemCount'
  >
}

function formatFunnelDate(value: string, language: string): string {
  const locale = language.startsWith('zh') ? 'zh-CN' : 'en-GB'
  return new Date(value).toLocaleString(locale)
}

export function PackagingSupplierFunnelStatusCell({
  supplier,
}: PackagingSupplierFunnelStatusCellProps) {
  const { t, i18n } = useTranslation('packagingSuppliers')
  const stage = derivePackagingSupplierFunnelStage(supplier)
  const label = t(funnelStageLabelKey(stage))

  if (stage === 'NOT_OPENED') {
    return <span>{label}</span>
  }

  let tooltip = label
  if (stage === 'OPENED' && supplier.magicLinkFirstOpenedAt) {
    tooltip = formatFunnelDate(supplier.magicLinkFirstOpenedAt, i18n.language)
  } else if (
    stage === 'ACKNOWLEDGED' &&
    supplier.ppwrComplianceAcknowledgedAt
  ) {
    tooltip = formatFunnelDate(
      supplier.ppwrComplianceAcknowledgedAt,
      i18n.language
    )
  } else if (
    stage === 'WITH_PACKAGING_ITEMS' ||
    stage === 'ALL_ITEMS_COMPLETE'
  ) {
    tooltip = formatStatFraction(
      supplier.completedPackagingItemCount ?? 0,
      supplier.totalPackagingItemCount ?? 0
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className='cursor-default'>{label}</span>
      </TooltipTrigger>
      <TooltipContent side='top'>{tooltip}</TooltipContent>
    </Tooltip>
  )
}
